/**
 * Text readability — pure, deterministic Flesch scoring. Safe to render
 * server-side. The syllable count is a heuristic (vowel groups with a
 * silent-e adjustment), so scores are estimates, not exact linguistics.
 */

export interface ReadabilityResult {
  words: number;
  sentences: number;
  syllables: number;
  characters: number;
  fleschReadingEase: number;
  fleschKincaidGrade: number;
  /** Estimated reading time in minutes (at 200 wpm). */
  readingTimeMin: number;
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/** Estimate the syllables in a single word (≥1 for any word with letters). */
export function countSyllables(word: string): number {
  const w = word.toLowerCase().replace(/[^a-z]/g, "");
  if (!w) return 0;
  if (w.length <= 3) return 1;
  const stripped = w
    .replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, "")
    .replace(/^y/, "");
  const groups = stripped.match(/[aeiouy]{1,2}/g);
  return Math.max(1, groups ? groups.length : 1);
}

const EMPTY: ReadabilityResult = {
  words: 0,
  sentences: 0,
  syllables: 0,
  characters: 0,
  fleschReadingEase: 0,
  fleschKincaidGrade: 0,
  readingTimeMin: 0,
};

export function analyze(text: string): ReadabilityResult {
  const trimmed = text.trim();
  if (!trimmed) return { ...EMPTY };

  const wordTokens = trimmed.split(/\s+/).filter(Boolean);
  const words = wordTokens.length;
  const characters = [...text].length;

  const sentenceMatches = trimmed.match(/[.!?]+/g);
  const sentences = Math.max(1, sentenceMatches ? sentenceMatches.length : 1);

  let syllables = 0;
  for (const token of wordTokens)
    syllables += Math.max(1, countSyllables(token));

  const wordsPerSentence = words / sentences;
  const syllablesPerWord = syllables / words;

  const fre = 206.835 - 1.015 * wordsPerSentence - 84.6 * syllablesPerWord;
  const fk = 0.39 * wordsPerSentence + 11.8 * syllablesPerWord - 15.59;

  return {
    words,
    sentences,
    syllables,
    characters,
    fleschReadingEase: round1(fre),
    fleschKincaidGrade: round1(fk),
    readingTimeMin: round1(words / 200),
  };
}

export type ReadingBand =
  | "very-easy"
  | "easy"
  | "fairly-easy"
  | "standard"
  | "fairly-difficult"
  | "difficult"
  | "very-difficult";

/** Map a Flesch Reading Ease score to a difficulty band key. */
export function readingBand(fre: number): ReadingBand {
  if (fre >= 90) return "very-easy";
  if (fre >= 80) return "easy";
  if (fre >= 70) return "fairly-easy";
  if (fre >= 60) return "standard";
  if (fre >= 50) return "fairly-difficult";
  if (fre >= 30) return "difficult";
  return "very-difficult";
}
